import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import EkaFoundationSection from "@/pages/partners-donors/components/EkaFoundationSection";
import { useSEOMeta, generateBreadcrumbSchema, generateWebPageSchema } from "@/components/SEO";

const partnerCategories = [
  { id: "all", label: "All Partners" },
  { id: "brand", label: "Brand Partners" },
  { id: "foundation", label: "Foundations" },
  { id: "institutional", label: "Institutional" },
];

const partners = [
  {
    id: 1,
    name: "European Union",
    category: "institutional",
    logo: "https://readdy.ai/api/search-image?query=European%20Union%20official%20logo%20emblem%20blue%20stars%20circle%20on%20white%20background%20clean%20minimal%20professional&width=200&height=120&seq=eu-logo-1&orientation=landscape",
    description: "Supporting sustainable development and humanitarian aid across Africa.",
    type: "Institutional Partner",
  },
  {
    id: 2,
    name: "World Health Organization",
    category: "institutional",
    logo: "https://readdy.ai/api/search-image?query=World%20Health%20Organization%20WHO%20logo%20emblem%20blue%20white%20clean%20minimal%20professional%20health&width=200&height=120&seq=who-logo-1&orientation=landscape",
    description: "Collaborating on public health initiatives and community wellness programs.",
    type: "Institutional Partner",
  },
  {
    id: 3,
    name: "United Nations",
    category: "institutional",
    logo: "https://readdy.ai/api/search-image?query=United%20Nations%20UN%20logo%20emblem%20blue%20white%20globe%20laurel%20wreath%20clean%20minimal%20professional&width=200&height=120&seq=un-logo-1&orientation=landscape",
    description: "Partnering on global development goals and humanitarian response.",
    type: "Institutional Partner",
  },
  {
    id: 4,
    name: "UNDP",
    category: "institutional",
    logo: "https://readdy.ai/api/search-image?query=UNDP%20United%20Nations%20Development%20Programme%20logo%20blue%20white%20clean%20minimal%20professional&width=200&height=120&seq=undp-logo-1&orientation=landscape",
    description: "Joint programs for poverty reduction and sustainable development.",
    type: "Institutional Partner",
  },
  {
    id: 5,
    name: "UN Women",
    category: "institutional",
    logo: "https://readdy.ai/api/search-image?query=UN%20Women%20United%20Nations%20gender%20equality%20logo%20clean%20minimal%20professional%20white%20background&width=200&height=120&seq=unwomen-logo-1&orientation=landscape",
    description: "Advancing gender equality and women empowerment in Cameroon.",
    type: "Institutional Partner",
  },
  {
    id: 6,
    name: "UNFPA",
    category: "institutional",
    logo: "https://readdy.ai/api/search-image?query=UNFPA%20United%20Nations%20Population%20Fund%20logo%20teal%20blue%20clean%20minimal%20professional%20white%20background&width=200&height=120&seq=unfpa-logo-1&orientation=landscape",
    description: "Supporting reproductive health and family planning programs.",
    type: "Institutional Partner",
  },
  {
    id: 7,
    name: "UNHCR",
    category: "institutional",
    logo: "https://readdy.ai/api/search-image?query=UNHCR%20United%20Nations%20Refugee%20Agency%20logo%20blue%20white%20clean%20minimal%20professional&width=200&height=120&seq=unhcr-logo-1&orientation=landscape",
    description: "Protecting and supporting displaced populations across the region.",
    type: "Institutional Partner",
  },
  {
    id: 8,
    name: "UNICEF",
    category: "institutional",
    logo: "https://readdy.ai/api/search-image?query=UNICEF%20United%20Nations%20Children%20Fund%20logo%20blue%20white%20clean%20minimal%20professional&width=200&height=120&seq=unicef-logo-1&orientation=landscape",
    description: "Improving child health, education, and protection outcomes.",
    type: "Institutional Partner",
  },
  {
    id: 9,
    name: "Gates Foundation",
    category: "foundation",
    logo: "https://readdy.ai/api/search-image?query=Bill%20Melinda%20Gates%20Foundation%20logo%20clean%20minimal%20professional%20white%20background%20philanthropy&width=200&height=120&seq=gates-logo-1&orientation=landscape",
    description: "Funding health innovation and disease prevention programs.",
    type: "Foundation",
  },
  {
    id: 10,
    name: "Open Society Foundation",
    category: "foundation",
    logo: "https://readdy.ai/api/search-image?query=Open%20Society%20Foundation%20logo%20clean%20minimal%20professional%20white%20background%20philanthropy&width=200&height=120&seq=osf-logo-1&orientation=landscape",
    description: "Supporting civil society and human rights initiatives.",
    type: "Foundation",
  },
  {
    id: 11,
    name: "Ministère de la Santé",
    category: "institutional",
    logo: "https://readdy.ai/api/search-image?query=Cameroon%20Ministry%20of%20Health%20government%20logo%20official%20seal%20clean%20minimal%20professional%20white%20background&width=200&height=120&seq=minsante-logo-1&orientation=landscape",
    description: "National health ministry partnership for community health programs.",
    type: "Government Partner",
  },
  {
    id: 12,
    name: "ILO",
    category: "institutional",
    logo: "https://readdy.ai/api/search-image?query=International%20Labour%20Organization%20ILO%20logo%20blue%20white%20clean%20minimal%20professional&width=200&height=120&seq=ilo-logo-1&orientation=landscape",
    description: "Promoting decent work and economic security for vulnerable populations.",
    type: "Institutional Partner",
  },
];

const whyPartnerPoints = [
  {
    icon: "ri-global-line",
    title: "Wide Program Reach",
    desc: "Tap into a wide range of programs and agenda-setting topics: human rights, climate change, education, food & water, LGBT+ rights, and many more.",
  },
  {
    icon: "ri-settings-3-line",
    title: "Tailored Engagement",
    desc: "The level of engagement is completely up to you. Our partnership can be tailored to fit your company DNA and strategic focus.",
  },
  {
    icon: "ri-team-line",
    title: "Collaborative Action",
    desc: "We believe in collaborative action, knowledge sharing and partnerships to improve protection and response work for people affected by displacement.",
  },
  {
    icon: "ri-leaf-line",
    title: "Sustainable Outcomes",
    desc: "We look to join forces, work in partnerships and create positive-sum games to ensure that needs of people affected by displacement, planet, peace and prosperity are balanced for sustainable outcomes for all.",
  },
];

export default function PartnersAndDonors() {
  useSEOMeta({
    title: "Partners & Donors - BHODEV Cameroon | UN Agencies, Foundations & Corporate Partners",
    description:
      "BHODEV Cameroon partners with UN agencies, international foundations, and corporate partners to drive sustainable development. Join our network of partners supporting health, education, and protection in Cameroon.",
    keywords:
      "BHODEV partners, BHODEV donors, Cameroon NGO partners, UN agencies Cameroon, humanitarian partners Cameroon, NGO funding Cameroon, corporate social responsibility Cameroon, BHODEV foundation partners",
    canonicalUrl: "https://bhodevcameroon.org/partners-donors",
    ogImage:
      "https://readdy.ai/api/search-image?query=diverse%20group%20of%20professionals%20shaking%20hands%20partnership%20collaboration%20meeting%20modern%20office%20environment%20warm%20natural%20light%20professional%20NGO%20development%20organization%20Cameroon%20Africa&width=1200&height=630&seq=partners-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "Partners & Donors", url: "https://bhodevcameroon.org/partners-donors" },
      ]),
      ...generateWebPageSchema(
        "Partners & Donors - BHODEV Cameroon",
        "BHODEV Cameroon partners with UN agencies, international foundations, and corporate partners to drive sustainable development.",
        "https://bhodevcameroon.org/partners-donors"
      ),
    },
  });

  const [activeCategory, setActiveCategory] = useState("all");
  const navigate = useNavigate();
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation();
  const { ref: whyRef, isVisible: whyVisible } = useScrollAnimation();
  const { ref: partnersRef, isVisible: partnersVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  const filtered = activeCategory === "all"
    ? partners
    : partners.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[480px] md:h-[560px] flex items-center justify-center overflow-hidden">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frenovation-and-reconstruction-of-bomaka-primary-school-buildings%2FIMG-20231209-WA0097.jpg?alt=media&token=31861194-44f4-40b4-a716-64d5349acdc6"
          alt="Partners and Donors"
          title="BHODEV Partners and Donors - UN Agencies, Foundations & Corporate Partners"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        <div
          ref={heroRef}
          className={`relative z-10 text-center px-4 transition-all duration-1000 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <span className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 text-primary text-xs font-semibold px-4 py-1.5 rounded-full mb-5 backdrop-blur-sm">
            <i className="ri-links-line"></i> About Us
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-5 leading-tight font-playfair">
            Partners &amp; Donors
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            A wide range of foundations and private sector partners support the work of Bogi Health Organization for Development Cameroon.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <button
              onClick={() => navigate("/partners-donors/brand-partners")}
              className="bg-primary text-white px-7 py-3 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 shadow-lg shadow-primary/30 whitespace-nowrap cursor-pointer"
            >
              Brand Partners
            </button>
            <a
              href="#become-partner"
              className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-7 py-3 rounded-full font-semibold text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Become a Partner
            </a>
          </div>
        </div>
      </section>

      {/* Why Partnership */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            ref={whyRef}
            className={`transition-all duration-700 ${whyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="text-center mb-14">
              <span className="text-primary text-sm font-semibold tracking-widest uppercase">Collaboration</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-4 font-playfair">Why Partnership?</h2>
              <p className="text-stone-500 max-w-2xl mx-auto text-base leading-relaxed">
                Complex problem solving requires fresh ideas, innovative thinking, and a willingness to cooperate in new ways.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              {whyPartnerPoints.map((point, i) => (
                <div
                  key={i}
                  className="flex gap-5 p-6 rounded-2xl bg-stone-50 hover:bg-secondary/30 transition-colors duration-300 group"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-xl shrink-0 group-hover:bg-primary/20 transition-colors duration-300">
                    <i className={`${point.icon} text-primary text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-900 mb-2 text-base">{point.title}</h3>
                    <p className="text-stone-500 text-sm leading-relaxed">{point.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Partnership types */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-8 text-white">
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-5">
                  <i className="ri-shake-hands-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3 font-playfair">Let&apos;s Join Forces in a Partnership</h3>
                <p className="text-white/80 text-sm leading-relaxed mb-5">
                  BHODEV strongly believes in the value of collaborative action, knowledge sharing and partnerships to improve protection and response work for people affected by displacement.
                </p>
                <a
                  href="#become-partner"
                  className="inline-flex items-center gap-2 bg-white text-primary px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-stone-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Partner With Us <i className="ri-arrow-right-line"></i>
                </a>
              </div>
              <div className="bg-stone-900 rounded-2xl p-8 text-white">
                <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl mb-5">
                  <i className="ri-earth-line text-2xl text-primary"></i>
                </div>
                <h3 className="text-xl font-bold mb-3 font-playfair">What&apos;s in it for me?</h3>
                <p className="text-stone-400 text-sm leading-relaxed mb-5">
                  For many companies, taking an interest in wider societal issues is not just a CSR initiative, but a fundamental part of making good business. Operating sustainably and making a positive impact has become key value margins for not only consumers but an important motivator for employees as well.
                </p>
                <a
                  href="#partners-grid"
                  className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-dark transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  View Our Partners <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      <section id="partners-grid" className="py-20 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div
            ref={partnersRef}
            className={`transition-all duration-700 ${partnersVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="text-center mb-10">
              <span className="text-primary text-sm font-semibold tracking-widest uppercase">Our Network</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-4 font-playfair">We Are Proud to Partner With</h2>
              <p className="text-stone-500 max-w-xl mx-auto text-sm leading-relaxed">
                Together with our global network of partners, we are creating lasting change in communities across Cameroon.
              </p>
            </div>

            {/* Filter tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {partnerCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.id
                      ? "bg-primary text-white shadow-lg shadow-primary/25"
                      : "bg-white text-stone-600 border border-stone-200 hover:border-primary hover:text-primary"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((partner, i) => (
                <div
                  key={partner.id}
                  className="bg-white rounded-2xl p-5 flex flex-col items-center text-center hover:shadow-md transition-all duration-300 group border border-stone-100 hover:border-primary/20"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="w-full h-16 flex items-center justify-center mb-4 overflow-hidden rounded-lg bg-stone-50">
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className="max-h-12 max-w-full object-contain"
                    />
                  </div>
                  <span className="inline-block bg-secondary/50 text-primary text-xs font-semibold px-2.5 py-1 rounded-full mb-2">
                    {partner.type}
                  </span>
                  <h3 className="font-bold text-stone-900 text-sm mb-1">{partner.name}</h3>
                  <p className="text-stone-500 text-xs leading-relaxed line-clamp-2">{partner.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EKA Foundation Feature Section */}
      <EkaFoundationSection />

      {/* Become a Partner CTA */}
      <section id="become-partner" className="py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            ref={ctaRef}
            className={`transition-all duration-700 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=community%20health%20workers%20team%20collaboration%20outdoor%20Cameroon%20Africa%20warm%20sunlight%20green%20landscape%20professional%20NGO%20development%20work&width=1200&height=400&seq=partners-cta-1&orientation=landscape"
                alt="Become a Partner"
                title="Become a BHODEV Partner - CSR & Corporate Social Responsibility"
                className="w-full h-64 md:h-80 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-14">
                <span className="text-primary text-xs font-semibold tracking-widest uppercase mb-3">Join Us</span>
                <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 font-playfair max-w-lg">
                  Ready to Make a Difference Together?
                </h2>
                <p className="text-white/70 text-sm max-w-md mb-6 leading-relaxed">
                  Dynamic partnerships between non-governmental organizations, duty bearers and private foundations and companies can and will become an important part of the solution.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="/contact"
                    className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-dark transition-all duration-300 shadow-lg shadow-primary/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-send-line"></i> Contact Us
                  </a>
                  <button
                    onClick={() => navigate("/partners-donors/brand-partners")}
                    className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-building-line"></i> Brand Partners
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
